"use client"

import React, { useEffect, useState } from 'react';
import { X, TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';
import apiService from '../../services/apiService';
import { TimeSeriesChart, StatusTimeline } from './time-series-chart';
import { ThroughputChart } from './throughput-chart';
import type { MachineHistorySeries } from '../../types';

/**
 * Per-machine history: one thin chart per metric on a shared time axis.
 *
 * Small multiples rather than one chart with ten series — a stack of thin
 * charts stays readable, whereas ten overlaid lines with different units does
 * not. Percentages are pinned to 0–100 so machines are visually comparable;
 * counts fit their own range.
 */

const RANGES = [
  { label: '6h', hours: 6 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 24 * 7 },
  { label: '30d', hours: 24 * 30 },
  { label: '90d', hours: 24 * 90 },
  { label: '1y', hours: 24 * 365 },
];

// Bucket widths offered in the UI. 'auto' lets the backend pick the finest
// bucket that fits the point budget for the chosen range.
const INTERVALS = [
  { label: 'Auto', value: 'auto' },
  { label: '15 min', value: '15m' },
  { label: 'Hourly', value: '1h' },
  { label: '6 hourly', value: '6h' },
  { label: 'Daily', value: 'day' },
  { label: 'Weekly', value: 'week' },
  { label: 'Monthly', value: 'month' },
];

// Running totals (images processed, patients synced, ...) can be read two ways.
const COUNTER_VIEWS = [
  { label: 'Work done', value: 'delta', hint: 'How much was processed in each period — shows throughput and stalls' },
  { label: 'Running total', value: 'total', hint: 'The cumulative value as the device reported it' },
];

// How several readings inside one bucket collapse to a single point.
const AGGREGATIONS = [
  { label: 'Peak', value: 'max', hint: 'Highest reading in each period — keeps spikes visible (gauges only; work counters always read the period end)' },
  { label: 'Average', value: 'avg', hint: 'Mean reading in each bucket — typical load' },
  { label: 'Lowest', value: 'min', hint: 'Lowest reading in each bucket' },
  { label: 'Latest', value: 'last', hint: 'Last reading in each bucket' },
  { label: 'Total', value: 'sum', hint: 'Sum of readings in each bucket' },
];

const METRIC_META: Record<
  string,
  { label: string; unit?: string; percent?: boolean; color: string }
> = {
  cpu_usage: { label: 'CPU usage', unit: '%', percent: true, color: '#2dd4bf' },
  memory_usage: { label: 'Memory usage', unit: '%', percent: true, color: '#60a5fa' },
  disk_usage: { label: 'Disk usage', unit: '%', percent: true, color: '#f59e0b' },
  temperature: { label: 'CPU temperature', unit: '°C', color: '#fb7185' },
  patients_sync_pending: { label: 'Patients pending sync', color: '#a78bfa' },
  patients_not_synced: { label: 'Patients not synced', color: '#f87171' },
  tasks_pending: { label: 'Tasks pending', color: '#facc15' },
  patients_synced: { label: 'Patients synced', unit: '/interval', color: '#34d399' },
  images_processed: { label: 'Images processed', unit: '/interval', color: '#22d3ee' },
  tasks_failed: { label: 'Failed tasks', color: '#ef4444' },
  cathode_tasks_failed: { label: 'Failed cathode tasks', color: '#f97316' },
  images_failed: { label: 'Images failed', unit: '/interval', color: '#e11d48' },
  system_status: { label: 'System status', color: '#0d9488' },
};

const metaFor = (name: string) =>
  METRIC_META[name] || { label: name.replace(/_/g, ' '), color: '#94a3b8' };

/** Direction of travel: compares the first and last thirds of the window. */
const trendOf = (values: (number | string | null)[]) => {
  const nums = values.filter((v): v is number => typeof v === 'number');
  if (nums.length < 6) return null;
  const third = Math.max(1, Math.floor(nums.length / 3));
  const head = nums.slice(0, third);
  const tail = nums.slice(-third);
  const mean = (a: number[]) => a.reduce((s, v) => s + v, 0) / a.length;
  const change = mean(tail) - mean(head);
  const base = Math.abs(mean(head)) || 1;
  const pct = (change / base) * 100;
  if (Math.abs(pct) < 8) return { dir: 'flat' as const, pct };
  return { dir: change > 0 ? ('up' as const) : ('down' as const), pct };
};

/** Compact labelled dropdown, styled to match the dark panel. */
const Picker: React.FC<{
  label: string;
  value: string;
  options: { label: string; value: string; hint?: string }[];
  onChange: (v: string) => void;
}> = ({ label, value, options, onChange }) => (
  <label className="flex items-center gap-1.5">
    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      title={options.find((o) => o.value === value)?.hint}
      className="text-xs rounded-lg px-2 py-1 outline-none"
      style={{
        backgroundColor: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        color: '#e2e8f0',
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} style={{ backgroundColor: '#1a1a1a' }}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);

interface Props {
  serialNumber: string;
  siteName?: string;
  onClose: () => void;
}

export const MachineHistoryPanel: React.FC<Props> = ({ serialNumber, siteName, onClose }) => {
  const [data, setData] = useState<MachineHistorySeries | null>(null);
  const [hours, setHours] = useState(24 * 14);
  const [interval, setInterval] = useState('day');
  const [aggregation, setAggregation] = useState('max');
  // Work is described as deltas: "what did this site process in this period".
  const [counterView, setCounterView] = useState('delta');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // One hover index shared by every chart, so they all read the same instant.
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [zoom, setZoom] = useState<[number, number] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiService
      .getMachineHistory(serialNumber, {
        hours, interval, aggregation,
        counter_view: counterView,
      })
      .then((res) => {
      if (cancelled) return;
      if (res.success) {
        setData(res.data);
        setError(null);
      } else {
        setError(res.error || 'Could not load history');
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [serialNumber, hours, interval, aggregation, counterView]);

  // Close on Escape, like the other overlays in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Close the expanded chart first, if one is open.
      if (expanded) setExpanded(null);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, expanded]);

  const timeLabels = (() => {
    if (!data || data.timestamps.length === 0) return { start: '', end: '' };
    const fmt = (iso: string) =>
      new Date(iso).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    return { start: fmt(data.timestamps[0]), end: fmt(data.timestamps[data.timestamps.length - 1]) };
  })();

  // Human name for the current bucket width, e.g. "day" or "hour". Used both
  // in the metric badge and in the chart's hover readout.
  const bucketLabel = (() => {
    const mins = data?.bucket_minutes ?? 0;
    if (!mins) return '';
    if (mins >= 10080) return 'week';
    if (mins >= 1440) return mins === 1440 ? 'day' : `${Math.round(mins / 1440)}d`;
    if (mins >= 60) return mins === 60 ? 'hour' : `${Math.round(mins / 60)}h`;
    return `${mins} min`;
  })();

  // Grouped by the question each metric answers, rather than one flat list:
  // work (what did it process), health (is it under stress), status (anything
  // stuck or broken). Each group wants reading differently.
  const sections = React.useMemo(() => {
    if (!data) return [] as { key: string; title: string; caption: string; metrics: string[] }[];
    const order = ['work', 'health', 'status'] as const;
    const titles: Record<string, [string, string]> = {
      work: ['Work', 'What this machine processed'],
      health: ['Health', 'Resource pressure over time'],
      status: ['Backlog & faults', 'What is queued, failing or unreported'],
    };
    const byGroup: Record<string, string[]> = { work: [], health: [], status: [] };
    Object.keys(data.series).forEach((name) => {
      const g = data.groups?.[name] || 'status';
      (byGroup[g] || byGroup.status).push(name);
    });
    // system_status reads last in its group; it is context, not a measurement.
    byGroup.status.sort((a, b) =>
      (a === 'system_status' ? 1 : 0) - (b === 'system_status' ? 1 : 0) || a.localeCompare(b));
    return order
      .filter((g) => byGroup[g].length)
      .map((g) => ({
        key: g,
        title: titles[g][0],
        caption: titles[g][1],
        metrics: byGroup[g],
      }));
  }, [data]);

  return (
    <>
      <div
        className="fixed inset-0 z-40 transition-opacity"
        style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
        onClick={onClose}
      />
      <div
        className="fixed inset-y-0 right-0 z-50 w-full max-w-3xl overflow-y-auto"
        style={{
          backgroundColor: '#141414',
          borderLeft: '1px solid rgba(255,255,255,0.08)',
          textAlign: 'left',
        }}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 px-6 py-4 flex items-start justify-between gap-4"
          style={{ backgroundColor: '#141414', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="min-w-0">
            <div className="text-base font-semibold truncate" style={{ color: '#ffffff' }}>
              {serialNumber}
            </div>
            <div className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
              {siteName || 'No site assigned'}
              {data && data.timestamps.length > 0 && (
                <> · {timeLabels.start} → {timeLabels.end} · {data.bucket_minutes}m buckets</>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              className="flex rounded-lg overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.1)' }}
            >
              {RANGES.map((r) => (
                <button
                  key={r.label}
                  onClick={() => { setHours(r.hours); setZoom(null); }}
                  className="px-2 py-1 text-xs transition-colors"
                  style={{
                    backgroundColor: hours === r.hours ? '#0d9488' : 'transparent',
                    color: hours === r.hours ? '#ffffff' : 'rgba(255,255,255,0.5)',
                  }}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5"
              style={{ color: 'rgba(255,255,255,0.5)' }}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Bucketing controls */}
        <div
          className="px-6 py-3 flex items-center gap-4 flex-wrap"
          style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
        >
          <Picker
            label="Group by"
            value={interval}
            options={INTERVALS}
            onChange={(v) => { setInterval(v); setZoom(null); }}
          />
          <Picker
            label="Show"
            value={aggregation}
            options={AGGREGATIONS}
            onChange={setAggregation}
          />
          <Picker
            label="Counters"
            value={counterView}
            options={COUNTER_VIEWS}
            onChange={(v) => { setCounterView(v); setZoom(null); }}
          />
          {data && data.timestamps.length > 0 && (
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {data.timestamps.length} points ·{' '}
              {data.bucket_minutes >= 1440
                ? `${Math.round(data.bucket_minutes / 1440)}d buckets`
                : data.bucket_minutes >= 60
                  ? `${Math.round(data.bucket_minutes / 60)}h buckets`
                  : `${data.bucket_minutes}m buckets`}
            </span>
          )}
        </div>

        <div className="p-6">
          {loading && (
            <div className="text-sm py-10 text-center" style={{ color: 'rgba(255,255,255,0.45)' }}>
              Loading history...
            </div>
          )}

          {error && !loading && (
            <div
              className="flex items-center gap-2 rounded-xl p-4 text-sm"
              style={{
                backgroundColor: 'rgba(239,68,68,0.1)',
                border: '1px solid rgba(239,68,68,0.3)',
                color: '#fca5a5',
              }}
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {!loading && !error && data && data.timestamps.length === 0 && (
            <div className="text-sm py-10 text-center" style={{ color: 'rgba(255,255,255,0.45)' }}>
              No readings in this window.
            </div>
          )}

          {!loading && !error && data && data.timestamps.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.35)' }}>
                Hover to read a value — all charts follow the same instant.
                Click a chart to expand it, or drag across one to zoom.
              </p>
              {/* Liveness + recent throughput: the questions a 15-minute
                  reporting interval answers best. A machine can report
                  perfectly while processing nothing, which no chart shows. */}
              {data.activity && (
                <div
                  className="rounded-xl p-4 mb-2"
                  style={{
                    backgroundColor: data.activity.reporting
                      ? 'rgba(13,148,136,0.08)'
                      : 'rgba(239,68,68,0.08)',
                    border: `1px solid ${data.activity.reporting
                      ? 'rgba(13,148,136,0.25)' : 'rgba(239,68,68,0.3)'}`,
                  }}
                >
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: data.activity.reporting ? '#2dd4bf' : '#f87171' }}
                      />
                      <span className="text-sm font-medium" style={{ color: '#ffffff' }}>
                        {data.activity.reporting ? 'Reporting' : 'Not reporting'}
                      </span>
                    </span>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      last reading{' '}
                      {data.activity.minutes_since_reading === null
                        ? 'never'
                        : data.activity.minutes_since_reading < 60
                          ? `${data.activity.minutes_since_reading}m ago`
                          : `${Math.floor(data.activity.minutes_since_reading / 60)}h ago`}
                    </span>
                    {Object.entries(data.activity.work).map(([name, w]) => (
                      <span key={name} className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                        {metaFor(name).label}{' '}
                        <span style={{ color: '#ffffff', fontWeight: 600 }}>
                          {w['24h'].toLocaleString()}
                        </span>{' '}
                        in 24h ·{' '}
                        <span style={{ color: '#e2e8f0' }}>{w['7d'].toLocaleString()}</span> in 7d
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {sections.map((section) => (
                <div key={section.key} className="pt-2">
                  <div className="flex items-baseline gap-3 mb-2">
                    <span
                      className="text-xs font-bold uppercase tracking-widest"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                      {section.title}
                    </span>
                    <span className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>
                      {section.caption}
                    </span>
                  </div>
                  <div className="space-y-3">
              {section.metrics.map((name) => {
                const meta = metaFor(name);
                const values = data.series[name] || [];
                const stat = data.summary[name];
                const isStatus = name === 'system_status';
                const isCounter = data.is_counter?.[name];
                // Bars and the "per interval" tag belong to deltas only; a
                // running total is a line like any other gauge.
                const isDelta = data.is_delta?.[name] ?? false;
                const trend = isStatus ? null : trendOf(values);

                if (!isStatus && !stat) return null;

                return (
                  <div
                    key={name}
                    className="rounded-xl p-4"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span
                          className="h-2 w-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: meta.color }}
                        />
                        <span className="text-sm font-medium truncate" style={{ color: '#e2e8f0' }}>
                          {meta.label}
                        </span>
                        {isCounter && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded"
                            style={{
                              backgroundColor: 'rgba(255,255,255,0.06)',
                              color: 'rgba(255,255,255,0.45)',
                            }}
                            title={isDelta
                              ? 'Work done in each period, not the running total'
                              : 'The cumulative value as the device reported it'}
                          >
                            {isDelta ? bucketLabel : 'running total'}
                          </span>
                        )}
                      </div>

                      {stat && (
                        <div
                          className="flex items-center gap-3 text-xs flex-shrink-0"
                          style={{ color: 'rgba(255,255,255,0.5)' }}
                        >
                          <span>
                            now{' '}
                            <span style={{ color: '#ffffff', fontWeight: 600 }}>
                              {stat.last}
                              {meta.unit === '%' ? '%' : ''}
                            </span>
                          </span>
                          <span>max {stat.max}</span>
                          <span>avg {stat.avg}</span>
                          {(aggregation === 'sum' || (isDelta && counterView === 'delta')) && (
                            <span>total {stat.total.toLocaleString()}</span>
                          )}
                          {trend && (
                            <span
                              className="flex items-center gap-0.5"
                              style={{
                                color:
                                  trend.dir === 'flat'
                                    ? 'rgba(255,255,255,0.4)'
                                    : trend.dir === 'up'
                                      ? '#fb923c'
                                      : '#4ade80',
                              }}
                              title={`${trend.pct > 0 ? '+' : ''}${trend.pct.toFixed(0)}% across the window`}
                            >
                              {trend.dir === 'up' ? (
                                <TrendingUp className="h-3 w-3" />
                              ) : trend.dir === 'down' ? (
                                <TrendingDown className="h-3 w-3" />
                              ) : (
                                <Minus className="h-3 w-3" />
                              )}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {isStatus ? (
                      <>
                        <div
                          onClick={() => setExpanded(name)}
                          style={{ cursor: 'zoom-in' }}
                        >
                          <StatusTimeline
                            timestamps={data.timestamps}
                            values={values.map((v) => (typeof v === 'string' ? v : null))}
                            height={26}
                            hoverIndex={hoverIndex}
                            onHoverIndexChange={setHoverIndex}
                            zoom={zoom}
                          />
                        </div>
                        <div
                          className="flex items-center gap-4 mt-2 text-xs"
                          style={{ color: 'rgba(255,255,255,0.4)' }}
                        >
                          {[
                            ['#0d9488', 'online'],
                            ['#d97706', 'degraded'],
                            ['rgba(255,255,255,0.08)', 'no contact'],
                          ].map(([c, l]) => (
                            <span key={l} className="flex items-center gap-1.5">
                              <span
                                className="h-2 w-2 rounded-sm"
                                style={{ backgroundColor: c }}
                              />
                              {l}
                            </span>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          {isDelta ? (
                            <ThroughputChart
                              timestamps={data.timestamps}
                              values={values.map((v) => (typeof v === 'number' ? v : null))}
                              color={meta.color}
                              unit={bucketLabel ? ` per ${bucketLabel}` : ''}
                              height={150}
                              hoverIndex={hoverIndex}
                              onHoverIndexChange={setHoverIndex}
                              zoom={zoom}
                              onZoomChange={setZoom}
                              onExpand={() => setExpanded(name)}
                              expandable
                            />
                          ) : (
                            <TimeSeriesChart
                              timestamps={data.timestamps}
                              values={values.map((v) => (typeof v === 'number' ? v : null))}
                              color={meta.color}
                              unit={meta.unit === '%' ? '%' : meta.unit === '°C' ? '°C' : ''}
                              percent={meta.percent}
                              height={150}
                              hoverIndex={hoverIndex}
                              onHoverIndexChange={setHoverIndex}
                              zoom={zoom}
                              onZoomChange={setZoom}
                              onExpand={() => setExpanded(name)}
                              expandable
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                          {stat && stat.gaps > 0 && (
                            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
                              {stat.gaps} gap{stat.gaps === 1 ? '' : 's'} — no reading recorded
                            </span>
                          )}
                          {/* The one genuine prediction in this data: disk
                              filling steadily gives weeks of warning. */}
                          {data.projection?.[name] && (
                            <span
                              className="text-xs px-2 py-0.5 rounded"
                              style={{
                                backgroundColor: 'rgba(245,158,11,0.12)',
                                color: '#fcd34d',
                              }}
                            >
                              filling {data.projection[name]!.per_day}%/day — full in ~
                              {data.projection[name]!.days_to_full} days
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Expanded chart: the same series, given the whole viewport. */}
      {expanded && data && (() => {
        const meta = metaFor(expanded);
        const values = data.series[expanded] || [];
        const stat = data.summary[expanded];
        const isStatus = expanded === 'system_status';
        const isCounter = data.is_counter?.[expanded];
        const isDelta = data.is_delta?.[expanded] ?? false;

        return (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-10"
            style={{ backgroundColor: 'rgba(0,0,0,0.85)', textAlign: 'left' }}
            onClick={() => setExpanded(null)}
          >
            <div
              className="w-full max-w-6xl rounded-2xl"
              style={{
                backgroundColor: '#141414',
                border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="px-6 py-4 flex items-center justify-between gap-4"
                style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <span
                    className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: meta.color }}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: '#ffffff' }}>
                      {meta.label}
                      {isCounter && (
                        <span
                          className="ml-2 text-xs font-normal"
                          style={{ color: 'rgba(255,255,255,0.4)' }}
                        >
                          {isDelta ? bucketLabel : 'running total'}
                        </span>
                      )}
                    </div>
                    <div className="text-xs" style={{ color: 'rgba(255,255,255,0.45)' }}>
                      {serialNumber}
                      {siteName ? ` · ${siteName}` : ''} · {data.bucket_minutes}m buckets
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-shrink-0">
                  {stat && (
                    <div
                      className="hidden sm:flex items-center gap-4 text-xs"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                      <span>min <span style={{ color: '#e2e8f0' }}>{stat.min}</span></span>
                      <span>avg <span style={{ color: '#e2e8f0' }}>{stat.avg}</span></span>
                      <span>max <span style={{ color: '#e2e8f0' }}>{stat.max}</span></span>
                      <span>now <span style={{ color: '#ffffff', fontWeight: 600 }}>{stat.last}</span></span>
                    </div>
                  )}
                  <Picker
                    label="Group by"
                    value={interval}
                    options={INTERVALS}
                    onChange={(v) => { setInterval(v); setZoom(null); }}
                  />
                  <Picker
                    label="Show"
                    value={aggregation}
                    options={AGGREGATIONS}
                    onChange={setAggregation}
                  />
                  <Picker
                    label="Counters"
                    value={counterView}
                    options={COUNTER_VIEWS}
                    onChange={(v) => { setCounterView(v); setZoom(null); }}
                  />
                  <div
                    className="flex rounded-lg overflow-hidden"
                    style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                  >
                    {RANGES.map((r) => (
                      <button
                        key={r.label}
                        onClick={() => { setHours(r.hours); setZoom(null); }}
                        className="px-2 py-1 text-xs"
                        style={{
                          backgroundColor: hours === r.hours ? '#0d9488' : 'transparent',
                          color: hours === r.hours ? '#ffffff' : 'rgba(255,255,255,0.5)',
                        }}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setExpanded(null)}
                    style={{ color: 'rgba(255,255,255,0.5)' }}
                    aria-label="Close expanded chart"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                {isStatus ? (
                  <StatusTimeline
                    timestamps={data.timestamps}
                    values={values.map((v) => (typeof v === 'string' ? v : null))}
                    height={48}
                    hoverIndex={hoverIndex}
                    onHoverIndexChange={setHoverIndex}
                    zoom={zoom}
                  />
                ) : isDelta ? (
                  <ThroughputChart
                    timestamps={data.timestamps}
                    values={values.map((v) => (typeof v === 'number' ? v : null))}
                    color={meta.color}
                    unit={bucketLabel ? ` per ${bucketLabel}` : ''}
                    height={Math.min(520, Math.max(300, window.innerHeight - 260))}
                    hoverIndex={hoverIndex}
                    onHoverIndexChange={setHoverIndex}
                    zoom={zoom}
                    onZoomChange={setZoom}
                  />
                ) : (
                  <TimeSeriesChart
                    timestamps={data.timestamps}
                    values={values.map((v) => (typeof v === 'number' ? v : null))}
                    color={meta.color}
                    unit={meta.unit === '%' ? '%' : meta.unit === '°C' ? '°C' : ''}
                    percent={meta.percent}
                    height={Math.min(520, Math.max(300, window.innerHeight - 260))}
                    hoverIndex={hoverIndex}
                    onHoverIndexChange={setHoverIndex}
                    zoom={zoom}
                    onZoomChange={setZoom}
                  />
                )}
                <div className="text-xs mt-3" style={{ color: 'rgba(255,255,255,0.35)' }}>
                  Drag across the chart to zoom into a time range · Esc to close
                  {stat && stat.gaps > 0 && ` · ${stat.gaps} gap${stat.gaps === 1 ? '' : 's'} with no reading`}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
};
